/**
 * User Management tab for FIN Settings.
 *
 * Reads and writes FIN's own `fin.users` table via /api/fin/users. This
 * directory belongs to the finance dashboard alone — creating a user here
 * grants access to FIN and nothing else, and has no effect on the CRMs.
 *
 * Only visible to users whose JWT role === 'ADMIN' (the API also
 * enforces this for every mutating call).
 */

import { useCallback, useMemo, useState } from 'react';
import { useFetch } from '@/lib/fin/use-fetch';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import {
  Search, Plus, Pencil, KeyRound, AlertCircle, Copy, Shield, X, Save, Check,
} from 'lucide-react';

interface User {
  id: string;
  email: string;
  name: string;
  role: 'ADMIN' | 'READ_ONLY' | 'SALES_PERSON' | string;
  entityScope: string[] | null;
  isActive: boolean;
  lastLogin: string | null;
  createdAt: string;
  updatedAt: string;
  twoFactorEnabled: boolean;
  has_password: boolean;
  entraLinked: boolean;
}

const ROLE_OPTIONS: { value: User['role']; label: string; desc: string }[] = [
  { value: 'ADMIN',        label: 'Admin',        desc: 'Full access including user management' },
  { value: 'READ_ONLY',    label: 'Read only',    desc: 'View all dashboards, no edits' },
  { value: 'SALES_PERSON', label: 'Sales person', desc: 'CRM-primary, limited FIN visibility' },
];

function roleBadgeVariant(role: string): 'default' | 'secondary' | 'outline' {
  if (role === 'ADMIN') return 'default';
  if (role === 'READ_ONLY') return 'secondary';
  return 'outline';
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (!isFinite(d.getTime())) return '—';
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

interface UserManagementTabProps {
  /**
   * The currently signed-in user. Used to hide / disable destructive
   * actions on their own row and to short-circuit the whole tab when
   * they're not an admin. If omitted, the tab still renders but all
   * mutating actions will hit the API's own role check.
   */
  currentUserId?: string | null;
  currentUserRole?: string | null;
}

export default function UserManagementTab({ currentUserId, currentUserRole }: UserManagementTabProps) {
  const [search, setSearch] = useState('');
  const [onlyActive, setOnlyActive] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<User | null>(null);
  const [resettingFor, setResettingFor] = useState<User | null>(null);
  const [generatedTempPassword, setGeneratedTempPassword] = useState<string | null>(null);

  // Filter server-side so large user lists stay quick. The filter
  // params are inlined into the URL so useFetch re-runs on change.
  const filterParam = useMemo(
    () => (onlyActive ? '?filter=active' : ''),
    [onlyActive],
  );

  const { data, loading, refresh } = useFetch<{ users: User[] }>(`/api/fin/users${filterParam}`);
  const users = data?.users || [];

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter(u =>
      u.email.toLowerCase().includes(q) ||
      u.name.toLowerCase().includes(q) ||
      u.role.toLowerCase().includes(q)
    );
  }, [users, search]);

  const isAdmin = currentUserRole === 'ADMIN' || currentUserRole === 'admin';

  if (!isAdmin) {
    return (
      <Card className="shadow-xs">
        <CardContent className="py-10 text-center space-y-2">
          <Shield size={28} className="mx-auto text-muted-foreground" />
          <p className="text-sm font-medium">Admin access required</p>
          <p className="text-xs text-muted-foreground">
            User management is restricted to users with the ADMIN role.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2 flex-1">
          <div className="relative max-w-sm flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by email, name, role..."
              value={search} onChange={e => setSearch(e.target.value)}
              className="pl-9 h-9"
            />
          </div>
          <div className="flex items-center gap-2 border-l pl-3">
            <Label htmlFor="active-only" className="text-xs text-muted-foreground whitespace-nowrap">Active only</Label>
            <Switch id="active-only" checked={onlyActive} onCheckedChange={setOnlyActive} />
          </div>
        </div>
        <Button size="sm" className="gap-2" onClick={() => setCreating(true)}>
          <Plus size={14} /> New user
        </Button>
      </div>

      {/* User table */}
      <Card className="shadow-xs">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Email</TableHead>
              <TableHead>Name</TableHead>
              <TableHead className="w-32">Role</TableHead>
              <TableHead className="w-40">Sign-in</TableHead>
              <TableHead className="w-28">Last login</TableHead>
              <TableHead className="w-20">Status</TableHead>
              <TableHead className="text-right w-28">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="py-8">
                  <Skeleton className="h-8 w-full" />
                </TableCell>
              </TableRow>
            )}
            {!loading && filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="py-10 text-center text-sm text-muted-foreground">
                  {search || onlyActive ? 'No users match the current filters.' : 'No users found.'}
                </TableCell>
              </TableRow>
            )}
            {filtered.map(u => {
              const isSelf = u.id === currentUserId;
              return (
                <TableRow key={u.id} className={!u.isActive ? 'opacity-60' : ''}>
                  <TableCell className="py-2 text-xs font-mono">{u.email}</TableCell>
                  <TableCell className="py-2 text-sm font-medium">
                    {u.name}
                    {isSelf && <Badge variant="outline" className="ml-2 text-[9px]">you</Badge>}
                  </TableCell>
                  <TableCell className="py-2">
                    <Badge variant={roleBadgeVariant(u.role)} className="text-[10px]">{u.role}</Badge>
                  </TableCell>
                  <TableCell className="py-2">
                    <div className="flex flex-wrap gap-1">
                      {u.has_password && (
                        <Badge variant="secondary" className="text-[9px]">Password</Badge>
                      )}
                      {u.entraLinked && (
                        <Badge variant="secondary" className="text-[9px]">Microsoft</Badge>
                      )}
                      {!u.has_password && !u.entraLinked && (
                        <span className="text-[10px] text-muted-foreground italic">no sign-in method</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="py-2 text-xs text-muted-foreground">{formatDate(u.lastLogin)}</TableCell>
                  <TableCell className="py-2">
                    <Badge variant={u.isActive ? 'default' : 'secondary'} className="text-[10px]">
                      {u.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right py-2">
                    <div className="inline-flex items-center gap-0.5">
                      <Button
                        variant="ghost" size="icon" className="h-6 w-6"
                        title="Reset password"
                        onClick={() => setResettingFor(u)}
                        disabled={isSelf}
                      >
                        <KeyRound size={12} />
                      </Button>
                      <Button
                        variant="ghost" size="icon" className="h-6 w-6"
                        title="Edit user"
                        onClick={() => setEditing(u)}
                      >
                        <Pencil size={12} />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>

      {/* Create dialog */}
      {creating && (
        <UserFormDialog
          user={null}
          onClose={() => setCreating(false)}
          onSaved={() => { setCreating(false); refresh(); }}
        />
      )}

      {/* Edit dialog */}
      {editing && (
        <UserFormDialog
          user={editing}
          currentUserId={currentUserId}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); refresh(); }}
        />
      )}

      {/* Reset-password dialog */}
      {resettingFor && (
        <ResetPasswordDialog
          user={resettingFor}
          onClose={() => { setResettingFor(null); setGeneratedTempPassword(null); }}
          onSaved={(tempPassword) => {
            if (tempPassword) setGeneratedTempPassword(tempPassword);
            else { setResettingFor(null); refresh(); }
          }}
        />
      )}

      {/* Temp password reveal dialog */}
      {generatedTempPassword && resettingFor && (
        <TempPasswordDialog
          user={resettingFor}
          tempPassword={generatedTempPassword}
          onClose={() => { setGeneratedTempPassword(null); setResettingFor(null); refresh(); }}
        />
      )}
    </div>
  );
}

/* ══════════════════════════ Create/Edit dialog ══════════════════════════ */

interface UserFormDialogProps {
  user: User | null;
  currentUserId?: string | null;
  onClose: () => void;
  onSaved: () => void;
}

function UserFormDialog({ user, currentUserId, onClose, onSaved }: UserFormDialogProps) {
  const isEdit = !!user;
  const [email, setEmail] = useState(user?.email || '');
  const [name, setName] = useState(user?.name || '');
  const [role, setRole] = useState<string>(user?.role || 'READ_ONLY');
  const [password, setPassword] = useState('');
  const [isActive, setIsActive] = useState(user?.isActive ?? true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isSelf = isEdit && user && currentUserId === user.id;

  const canSave = email.trim() !== '' && name.trim() !== '' && (isEdit || password.length >= 8);

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    setError(null);
    try {
      const token = localStorage.getItem('fin_auth_token');
      const headers = {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };
      const payload: Record<string, any> = {
        name: name.trim(),
        role,
        isActive,
      };
      if (isEdit) {
        payload.id = user!.id;
        // On edit we can't change email (it's the login identifier)
      } else {
        payload.email = email.trim();
        payload.password = password;
      }
      const res = await fetch('/api/fin/users', {
        method: isEdit ? 'PATCH' : 'POST',
        headers,
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.error || `HTTP ${res.status}`);
      }
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={o => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{isEdit ? `Edit user: ${user!.email}` : 'New user'}</DialogTitle>
          <DialogDescription className="text-xs">
            {isEdit
              ? 'Updates the shared user record used by CRM and FIN.'
              : 'Creates a new record in the shared User table. The user will be able to sign in to every app in the access list below.'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Email</Label>
            <Input
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="person@wise-wolves.com"
              disabled={isEdit}
              type="email"
              autoComplete="off"
            />
            {isEdit && <p className="text-[10px] text-muted-foreground">Email is the login identifier — use a password reset flow to change it.</p>}
          </div>
          <div className="space-y-1.5">
            <Label>Name</Label>
            <Input value={name} onChange={e => setName(e.target.value)} placeholder="Full name" />
          </div>
          {!isEdit && (
            <div className="space-y-1.5">
              <Label>Initial password</Label>
              <Input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="Min 8 characters"
                autoComplete="new-password"
              />
              <p className="text-[10px] text-muted-foreground">Share this via a secure channel. The user should change it on first login.</p>
            </div>
          )}
          <div className="space-y-1.5">
            <Label>Role</Label>
            <Select value={role} onValueChange={setRole} disabled={!!isSelf}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {ROLE_OPTIONS.map(r => (
                  <SelectItem key={r.value} value={r.value}>
                    <div className="flex flex-col">
                      <span>{r.label}</span>
                      <span className="text-[10px] text-muted-foreground">{r.desc}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {isSelf && <p className="text-[10px] text-muted-foreground">You can't change your own role away from ADMIN.</p>}
          </div>
          {isEdit && (
            <div className="flex items-center justify-between border-t pt-3">
              <div>
                <Label htmlFor="user-is-active">Active</Label>
                {isSelf && <p className="text-[10px] text-muted-foreground">You can't deactivate your own account.</p>}
              </div>
              <Switch
                id="user-is-active"
                checked={isActive}
                onCheckedChange={setIsActive}
                disabled={!!isSelf}
              />
            </div>
          )}

          {error && (
            <div className="text-xs text-destructive flex items-center gap-1">
              <AlertCircle size={12} /> {error}
            </div>
          )}

          <div className="flex justify-end gap-2 border-t pt-3">
            <Button variant="outline" onClick={onClose} disabled={saving}>
              <X size={14} className="mr-1" /> Cancel
            </Button>
            <Button onClick={handleSave} disabled={!canSave || saving}>
              <Save size={14} className="mr-1" /> {saving ? 'Saving...' : isEdit ? 'Save' : 'Create'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ══════════════════════════ Reset password dialog ══════════════════════════ */

interface ResetPasswordDialogProps {
  user: User;
  onClose: () => void;
  /** Called with tempPassword when the "generate" path is used, or with null otherwise. */
  onSaved: (tempPassword: string | null) => void;
}

function ResetPasswordDialog({ user, onClose, onSaved }: ResetPasswordDialogProps) {
  const [mode, setMode] = useState<'generate' | 'set'>('generate');
  const [newPassword, setNewPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSubmit = mode === 'generate' || (mode === 'set' && newPassword.length >= 8);

  const handleSubmit = async () => {
    setSaving(true);
    setError(null);
    try {
      const token = localStorage.getItem('fin_auth_token');
      const res = await fetch('/api/fin/users/reset-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(
          mode === 'generate'
            ? { id: user.id, generate: true }
            : { id: user.id, newPassword },
        ),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.error || `HTTP ${res.status}`);
      }
      const j = await res.json();
      onSaved(j.tempPassword || null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reset password');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={o => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Reset password</DialogTitle>
          <DialogDescription className="text-xs">
            For <span className="font-mono">{user.email}</span>. The new password takes effect immediately.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setMode('generate')}
              className={`flex-1 rounded border px-3 py-2 text-xs ${
                mode === 'generate' ? 'bg-primary text-primary-foreground border-primary' : 'bg-background border-border'
              }`}
            >
              Generate temp password
            </button>
            <button
              type="button"
              onClick={() => setMode('set')}
              className={`flex-1 rounded border px-3 py-2 text-xs ${
                mode === 'set' ? 'bg-primary text-primary-foreground border-primary' : 'bg-background border-border'
              }`}
            >
              Set a specific password
            </button>
          </div>
          {mode === 'set' && (
            <div className="space-y-1.5">
              <Label>New password</Label>
              <Input
                type="password"
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                placeholder="Min 8 characters"
                autoComplete="new-password"
              />
            </div>
          )}
          {mode === 'generate' && (
            <p className="text-xs text-muted-foreground">
              A 14-character random password will be shown once in the next dialog. Copy it immediately — it will not be shown again.
            </p>
          )}
          {error && (
            <div className="text-xs text-destructive flex items-center gap-1">
              <AlertCircle size={12} /> {error}
            </div>
          )}
          <div className="flex justify-end gap-2 border-t pt-3">
            <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={!canSubmit || saving}>
              {saving ? 'Working...' : 'Reset'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ══════════════════════════ Temp password reveal dialog ══════════════════════════ */

function TempPasswordDialog({ user, tempPassword, onClose }: { user: User; tempPassword: string; onClose: () => void }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(tempPassword);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* ignore */
    }
  };

  return (
    <Dialog open onOpenChange={o => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Temporary password generated</DialogTitle>
          <DialogDescription className="text-xs">
            Share this with <span className="font-mono">{user.email}</span> via a secure channel. It will not be shown again.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="rounded border bg-muted/40 px-3 py-3 font-mono text-sm break-all select-all">
            {tempPassword}
          </div>
          <Button onClick={handleCopy} className="w-full gap-2">
            <Copy size={14} /> {copied ? 'Copied!' : 'Copy to clipboard'}
          </Button>
          <div className="flex justify-end pt-1">
            <Button variant="outline" onClick={onClose}>Done</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
